<template>
    <v-container class="pa-2">
        <v-row dense>
            <v-col cols="12">

                <!-- ── Header + Toggle segmenti ─────────────────────────── -->
                <v-card class="rounded-xl elevation-2 px-3 py-2 mb-3">
                    <div v-if="!isAdmin || congregazioni.length <= 1" class="text-subtitle-2 font-weight-bold primary--text text-center mb-2">
                        Programma {{ $store.state.congregazioneCorrente ? $store.state.congregazioneCorrente.nome : '' }}
                    </div>
                    <div v-if="isAdmin && congregazioni.length > 1" class="d-flex justify-center mb-1">
                        <v-autocomplete
                            ref="congSelect"
                            v-model="congregazioneVista"
                            :items="congregazioni"
                            item-text="nome"
                            item-value="id"
                            outlined dense hide-details
                            color="primary"
                            label="Programma"
                            class="cong-select"
                            style="max-width: 230px;"
                            @change="onCongChange"
                        >
                            <template v-slot:selection="{ item }">
                                <span class="font-weight-bold primary--text" style="font-size:0.875rem;width:100%;text-align:center">
                                    {{ item.nome }}
                                </span>
                            </template>
                        </v-autocomplete>
                    </div>
                    <div class="d-flex justify-center">
                        <v-btn-toggle v-if="!isCollaboratore" v-model="tipoProgramma" mandatory dense borderless color="primary"
                                      class="tipo-toggle" @change="checkTipoProgramma">
                            <v-btn value="0" small class="tipo-btn">
                                <v-icon x-small left class="tipo-icon">mdi-home-outline</v-icon><span class="tipo-label">Interni</span>
                            </v-btn>
                            <v-btn v-if="puoVedereAltriOratori" value="1" small class="tipo-btn">
                                <v-icon x-small left class="tipo-icon">mdi-earth</v-icon><span class="tipo-label">Esterni</span>
                            </v-btn>
                            <v-btn value="2" small class="tipo-btn">
                                <v-icon x-small left class="tipo-icon">mdi-account-outline</v-icon><span class="tipo-label">Personali</span>
                            </v-btn>
                            <v-btn v-if="isCoordinatore && circoscrizionePrenotazione" value="3" small class="tipo-btn">
                                <v-icon x-small left class="tipo-icon">mdi-calendar-clock</v-icon><span class="tipo-label">Prenotazioni</span>
                            </v-btn>
                        </v-btn-toggle>
                    </div>
                </v-card>

                <!-- ── Header mese (solo interno/esterno) ───────────────── -->
                <v-card v-if="isCalendario" class="mb-2 rounded-xl elevation-2 px-3 py-2">
                    <div class="d-flex align-center">
                        <v-btn icon small text color="primary" @click="changeMonth(-1)">
                            <v-icon>mdi-chevron-left</v-icon>
                        </v-btn>
                        <v-btn icon small text color="success" @click="goToToday" class="ml-1">
                            <v-icon small>mdi-calendar-today</v-icon>
                        </v-btn>
                        <v-btn icon small text color="primary" :loading="loading" @click="doRefresh" class="ml-1">
                            <v-icon small>mdi-refresh</v-icon>
                        </v-btn>
                        <v-btn v-if="isSpostamentoAbilitato" icon small text color="deep-purple" @click="openSpostamentoMassivo" class="ml-1">
                            <v-icon small>mdi-calendar-arrow-right</v-icon>
                        </v-btn>
                        <span class="text-subtitle-2 font-weight-bold primary--text text-capitalize flex-grow-1 text-center">
                            {{ monthTitle }}
                        </span>
                        <!-- Export PDF (solo interno, solo coordinator) -->
                        <template v-if="isCoordinatore && tipoProgramma === '0'">
                            <v-btn v-if="!exportFilePdf" icon color="pink lighten-1" class="mr-1"
                                   :loading="loadingPdf" @click="esportaPdf">
                                <v-icon>mdi-file-pdf-box</v-icon>
                            </v-btn>
                            <v-btn v-else icon color="error" class="mr-1"
                                   @click="openExportFile('pdf')">
                                <v-icon>mdi-magnify</v-icon>
                            </v-btn>
                        </template>
                        <!-- Export Excel (interno/esterno, solo coordinator) -->
                        <template v-if="isCoordinatore">
                            <v-btn v-if="!exportFileExcel" icon color="green darken-1" class="mr-1"
                                   :loading="loadingExcel" @click="esportaExcel">
                                <v-icon>mdi-microsoft-excel</v-icon>
                            </v-btn>
                            <v-btn v-else icon color="green darken-1" class="mr-1"
                                   @click="openExportFile('excel')">
                                <v-icon>mdi-magnify</v-icon>
                            </v-btn>
                        </template>
                        <!-- Export NWS -->
                        <v-btn v-if="(isAdmin || $store.state.oratore.isAssistente) && tipoProgramma === '0'" icon class="mr-1"
                               :loading="loadingCsv" @click="dialogNws = true">
                            <img src="@/assets/nws_logo.webp" width="18" height="18" style="border-radius:4px;background:linear-gradient(rgb(50,143,196),rgb(78,197,212));padding:2px" />
                        </v-btn>
                        <v-btn icon small text color="primary" @click="changeMonth(1)">
                            <v-icon>mdi-chevron-right</v-icon>
                        </v-btn>
                    </div>
                    <v-progress-linear v-if="loading" indeterminate color="primary" height="2" class="mt-1" />
                </v-card>

                <!-- ══════════════════ CALENDARIO ══════════════════════════ -->
                <template v-if="isCalendario">

                    <!-- Griglia -->
                    <v-card class="rounded-xl elevation-2 px-2 pt-2 pb-1 mb-3">
                        <v-row no-gutters class="mb-1">
                            <v-col v-for="day in weekdays" :key="day" class="text-center">
                                <span class="text-caption font-weight-bold grey--text">{{ day }}</span>
                            </v-col>
                        </v-row>
                        <v-divider class="mb-1" />
                        <v-row v-for="(week, wi) in calendarDays" :key="wi" no-gutters>
                            <v-col v-for="(day, di) in week" :key="di" class="pa-1">
                                <div class="cal-cella d-flex align-center justify-center rounded-circle"
                                     :class="classeCella(day)" :style="stileCella(day)"
                                     @click="clickDate(day)">
                                    <span class="text-caption font-weight-medium">{{ day.dayOfMonth }}</span>
                                </div>
                            </v-col>
                        </v-row>
                    </v-card>

                    <!-- Card giorno selezionato (solo interno/esterno) -->
                    <v-card class="rounded-xl elevation-2 pb-2"
                            v-if="selectedDate && (tipoProgramma === '0' || tipoProgramma === '1')">
                        <div class="d-flex align-center px-3 py-2">
                            <v-icon size="15" color="primary" class="mr-2">mdi-calendar-check</v-icon>
                            <span class="text-body-2 font-weight-bold text-capitalize">
                                {{ selectedDate | fmtDate }}
                            </span>
                            <v-chip v-if="selectedDayEvents.length" x-small color="primary" class="ml-2">
                                {{ selectedDayEvents.length }}
                            </v-chip>
                            <v-spacer />
                            <template v-if="isCoordinatore">
                                <v-btn x-small depressed rounded color="primary" class="mr-1"
                                       :disabled="tipoProgramma === '0' && selectedDayEvents.length > 0"
                                       @click="goToNewStandardProgramma">
                                    <v-icon x-small left>mdi-plus</v-icon>Nuovo
                                </v-btn>
                                <v-btn x-small depressed rounded color="secondary"
                                       :disabled="tipoProgramma === '0' && selectedDayEvents.length > 0"
                                       @click="goToNewExtraProgramma">
                                    <v-icon x-small left>mdi-star-outline</v-icon>Extra
                                </v-btn>
                            </template>
                        </div>
                        <v-divider />

                        <!-- Quick actions eventi speciali (solo interno, coordinatore, solo se giornata vuota) -->
                        <div v-if="isCoordinatore && tipoProgramma === '0' && selectedDayEvents.length === 0"
                             class="d-flex px-2 pt-2 pb-1" style="gap:6px">
                            <v-card flat outlined class="evento-quick-btn flex-grow-1 pa-1"
                                    @click="saveNewProgramma('isCongresso')">
                                <v-icon color="deep-orange darken-4" size="18">mdi-calendar-star</v-icon>
                                <span class="evento-quick-label text-center mt-1">Assemblea/<br>Congresso</span>
                            </v-card>
                            <v-card flat outlined class="evento-quick-btn flex-grow-1 pa-1"
                                    @click="saveNewProgramma('isVisita')">
                                <v-icon color="primary" size="18">mdi-account-tie</v-icon>
                                <span class="evento-quick-label text-center mt-1">Visita<br>Sorveg.</span>
                            </v-card>
                            <v-card flat outlined class="evento-quick-btn flex-grow-1 pa-1"
                                    @click="saveNewProgramma('isSpeciale')">
                                <v-icon color="purple darken-2" size="18">mdi-star-outline</v-icon>
                                <span class="evento-quick-label text-center mt-1">Evento<br>Speciale</span>
                            </v-card>
                        </div>

                        <!-- Nessun evento -->
                        <div v-if="selectedDayEvents.length === 0"
                             class="text-center text-caption grey--text py-4">
                            Nessun appuntamento in programma
                        </div>

                        <!-- Lista eventi giorno selezionato -->
                        <template v-for="(event, idx) in selectedDayEvents">

                            <!-- Evento speciale -->
                            <div v-if="event.programma.isCongresso || event.programma.isVisita || event.programma.isSpeciale"
                                 :key="'sp-'+idx"
                                 class="pratica-card pratica-speciale mx-2 my-2"
                                 @click.stop="openActionSheet($event, event.programma)">
                                <div class="pratica-corpo flex-grow-1">
                                    <div class="d-flex align-center">
                                        <div class="flex-grow-1">
                                            <span class="pratica-cliente">
                                                <template v-if="event.programma.isCongresso">Assemblea/Congresso</template>
                                                <template v-else-if="event.programma.isVisita">
                                                    Visita del Sorvegliante
                                                    <span v-if="sorvegliante" class="pratica-sorv"> — {{ sorvegliante }}</span>
                                                </template>
                                                <template v-else>Evento Speciale</template>
                                            </span>
                                            <div v-if="event.programma.isVisita" class="pratica-tema-visita mt-1">
                                                <v-icon x-small color="white" class="mr-1" style="opacity:0.7">mdi-book-open-outline</v-icon>
                                                <template v-if="event.programma.teamVisita">{{ event.programma.teamVisita }}</template>
                                                <template v-else><em style="opacity:0.6">Tema non impostato</em></template>
                                            </div>
                                        </div>
                                        <v-icon small color="white" style="opacity:0.5">mdi-dots-vertical</v-icon>
                                    </div>
                                </div>
                            </div>

                            <!-- Evento standard / extra -->
                            <div v-else :key="'ev-'+idx"
                                 class="pratica-card mx-2 my-2"
                                 :class="event.programma.isProvvisorio ? 'pratica-provvisorio' : 'pratica-normale'"
                                 @click.stop="handleClickCalendario($event, event.programma)">
                                <div class="pratica-corpo flex-grow-1">
                                    <div class="d-flex align-center justify-space-between mb-1">
                                        <div class="d-flex align-center" style="gap:6px;min-width:0">
                                            <span class="pratica-cliente">
                                                <template v-if="event.programma.oratore">{{ event.programma.oratore.nominativo }}</template>
                                                <template v-else-if="event.programma.isExtra">{{ event.programma.oratoreExtra }}</template>
                                            </span>
                                            <span v-if="event.programma.oratore && event.programma.oratore.telefono"
                                                  class="d-flex align-center pratica-tel"
                                                  @click.stop="openTel(event.programma.oratore.telefono)">
                                                <v-icon size="13" color="white" style="opacity:0.75" class="mr-1">mdi-phone-outline</v-icon>
                                                <span style="font-size:0.72rem;opacity:0.85">{{ event.programma.oratore.telefono }}</span>
                                            </span>
                                        </div>
                                        <div class="d-flex align-center">
                                            <v-chip v-if="event.programma.oratore && event.programma.oratore.isAnziano"     x-small dark color="teal" class="mr-1">A</v-chip>
                                            <v-chip v-if="event.programma.oratore && event.programma.oratore.isSM"          x-small dark color="blue darken-1" class="mr-1">SM</v-chip>
                                            <v-chip v-if="event.programma.oratore && event.programma.oratore.isAssistente"  x-small dark color="grey darken-2" class="mr-1">ASSISTENTE</v-chip>
                                            <v-chip v-if="event.programma.oratore && event.programma.oratore.isCoordinatore" x-small dark color="deep-orange darken-4" class="mr-1">ADM</v-chip>
                                            <v-chip v-if="event.programma.oratore && event.programma.oratore.isAdmin"       x-small dark color="orange darken-1" class="mr-1">APP-ADM</v-chip>
                                            <v-icon small color="white" style="opacity:0.5">mdi-dots-vertical</v-icon>
                                        </div>
                                    </div>
                                    <div class="pratica-descrizione d-flex align-center mt-1">
                                        <v-icon x-small color="white" class="mr-1" style="opacity:0.7">mdi-home-city-outline</v-icon>
                                        <template v-if="event.programma.isExtra">{{ event.programma.congregazioneExtra }}</template>
                                        <template v-else-if="tipoProgramma === '0' && event.programma.congregazioneEsterna">{{ event.programma.congregazioneEsterna.nome }}</template>
                                        <template v-else-if="event.programma.congregazione">{{ event.programma.congregazione.nome }}</template>
                                    </div>
                                    <div v-if="event.programma.discorso" class="pratica-note d-flex align-center mt-1">
                                        <v-icon x-small color="white" class="mr-1" style="opacity:0.6">mdi-book-open-outline</v-icon>
                                        <span class="pratica-badge badge-schema mr-1">{{ event.programma.discorso.diId === 99999 ? '-' : event.programma.discorso.diId }}</span>
                                        <span class="pratica-tema">{{ event.programma.discorso.tema }}</span>
                                    </div>
                                    <div v-if="event.programma.cantico" class="pratica-note d-flex align-center mt-1">
                                        <v-icon x-small color="white" class="mr-1" style="opacity:0.6">mdi-music-note</v-icon>
                                        <span style="font-size:0.72rem;opacity:0.85">Cantico {{ event.programma.cantico }}</span>
                                    </div>
                                    <div v-if="event.programma.isProvvisorio" class="d-flex align-center justify-space-between mt-2">
                                        <div class="da-confermare">
                                            <v-icon x-small color="#E65100" class="mr-1">mdi-clock-alert-outline</v-icon>
                                            Da confermare
                                        </div>
                                        <div v-if="tipoProgramma === '0' && event.programma.congregazioneEsterna && event.programma.congregazioneEsterna.coordinatore"
                                             class="attesa-nome">
                                            <v-icon x-small class="mr-1" style="opacity:0.6">mdi-account-clock-outline</v-icon>
                                            {{ event.programma.congregazioneEsterna.coordinatore.nominativo }}
                                        </div>
                                    </div>
                                    <!-- Contatti esterno -->
                                    <div v-if="tipoProgramma === '1' && hasContatti(event.programma)"
                                         class="d-flex mt-2 pt-1" style="border-top:1px solid rgba(255,255,255,0.2)">
                                        <v-btn v-if="event.programma.oratore && event.programma.oratore.telefono"
                                               icon x-small color="white" class="mr-1"
                                               @click.stop="openTel(event.programma.oratore.telefono)">
                                            <v-icon small>mdi-phone-outline</v-icon>
                                        </v-btn>
                                        <v-btn v-if="event.programma.oratore && event.programma.oratore.mail"
                                               icon x-small color="white" class="mr-1"
                                               @click.stop="openMail(event.programma.oratore.mail)">
                                            <v-icon small>mdi-email-outline</v-icon>
                                        </v-btn>
                                        <v-btn v-if="event.programma.congregazione && event.programma.congregazione.latitudine"
                                               icon x-small color="white"
                                               @click.stop="openMaps(event.programma.congregazione.latitudine, event.programma.congregazione.longitudine)">
                                            <v-icon small>mdi-map-marker-outline</v-icon>
                                        </v-btn>
                                    </div>
                                    <!-- Mappa congr. esterna (interno) -->
                                    <div v-if="tipoProgramma === '0' && !event.programma.isExtra && event.programma.congregazioneEsterna && event.programma.congregazioneEsterna.latitudine"
                                         class="d-flex align-center mt-2 pt-1" style="border-top:1px solid rgba(255,255,255,0.2)">
                                        <v-btn icon x-small color="white" class="mr-2"
                                               @click.stop="openMaps(event.programma.congregazioneEsterna.latitudine, event.programma.congregazioneEsterna.longitudine)">
                                            <v-icon small>mdi-map-marker-outline</v-icon>
                                        </v-btn>
                                        <span style="font-size:0.68rem;color:rgba(255,255,255,0.6);">
                                            {{ event.programma.congregazioneEsterna.indirizzo }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </template>
                    </v-card>


                </template>

                <!-- ══════════════════ PERSONALI ═══════════════════════════ -->
                <template v-if="tipoProgramma === '2'">
                    <v-card class="rounded-xl elevation-2 px-3 py-2 mb-3">
                        <!-- Combo privilegiati: scelta libera, niente pre-selezionato -->
                        <v-select v-if="puoVedereAltriOratori"
                                  v-model="oratorePersonale"
                                  :items="listaOratori"
                                  :loading="loadingOratori"
                                  :no-data-text="loadingOratori ? 'Caricamento...' : 'Nessun oratore'"
                                  item-text="nominativo"
                                  return-object
                                  outlined rounded dense hide-details
                                  label="Oratore"
                                  placeholder="Scegli oratore"
                                  prepend-inner-icon="mdi-account-outline"
                                  class="mb-3"
                                  @change="loadPersonali" />
                        <!-- Combo fissa per utenti senza privilegi -->
                        <v-select v-else
                                  :value="$store.state.oratore"
                                  :items="[$store.state.oratore]"
                                  item-text="nominativo"
                                  return-object
                                  outlined rounded dense hide-details
                                  label="Oratore"
                                  prepend-inner-icon="mdi-account-outline"
                                  disabled
                                  class="mb-3" />
                        <div class="d-flex align-center justify-space-between">
                            <span class="text-caption grey--text">Mostra:</span>
                            <v-btn-toggle v-model="periodoFuturo" mandatory dense borderless color="primary"
                                          class="periodo-toggle" @change="loadPersonali">
                                <v-btn :value="false" x-small class="periodo-btn">Tutti</v-btn>
                                <v-btn :value="true"  x-small class="periodo-btn">Futuri</v-btn>
                            </v-btn-toggle>
                        </div>
                    </v-card>

                    <div v-if="loadingPersonali" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary" size="28" />
                    </div>
                    <template v-else-if="programmiPersonali.length > 0">
                        <div v-for="(p, i) in programmiPersonali" :key="'pp-'+i"
                             class="pratica-card mb-2"
                             :class="classeCardPersonale(p)"
                             @click.stop="handleClickPersonale($event, p)">
                            <div class="pratica-corpo flex-grow-1">
                                <div class="d-flex align-center justify-space-between mb-1">
                                    <span class="pratica-data">{{ p.data | fmtDateYear }}</span>
                                    <v-chip x-small dark class="chip-tipo">
                                        <v-icon x-small left>{{ p.tipoPersonale === 'interno' ? 'mdi-home-outline' : 'mdi-earth' }}</v-icon>
                                        {{ p.tipoPersonale === 'interno' ? 'interno' : 'esterno' }}
                                    </v-chip>
                                </div>
                                <div class="pratica-descrizione d-flex align-center">
                                    <v-icon x-small color="white" class="mr-1" style="opacity:0.7">mdi-home-city-outline</v-icon>
                                    <span v-if="p.tipoPersonale === 'interno' && p.congregazioneEsterna">{{ p.congregazioneEsterna.nome }}</span>
                                    <span v-else-if="p.isExtra">{{ p.congregazioneExtra }}</span>
                                    <span v-else-if="p.congregazione">{{ p.congregazione.nome }}</span>
                                </div>
                                <div v-if="p.discorso" class="pratica-note d-flex align-center mt-1">
                                    <v-icon x-small color="white" class="mr-1" style="opacity:0.6">mdi-book-open-outline</v-icon>
                                    <span class="pratica-badge badge-schema mr-1">{{ p.discorso.diId === 99999 ? '-' : p.discorso.diId }}</span>
                                    <span class="pratica-tema">{{ p.discorso.tema }}</span>
                                </div>
                                <div v-if="p.isProvvisorio" class="da-confermare mt-2">
                                    <v-icon x-small color="#E65100" class="mr-1">mdi-clock-alert-outline</v-icon>
                                    Da confermare
                                </div>
                            </div>
                        </div>
                    </template>
                    <v-card v-else class="rounded-xl elevation-2 px-3 py-4 text-center">
                        <v-icon color="grey lighten-1" size="36">mdi-calendar-blank-outline</v-icon>
                        <div class="text-caption grey--text mt-2">Nessun appuntamento in programma</div>
                    </v-card>
                </template>

                <!-- ══════════════════ PRENOTAZIONI ════════════════════════ -->
                <template v-if="tipoProgramma === '3'">
                    <div v-if="loadingPrenotazioni" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary" size="28" />
                    </div>
                    <template v-else-if="prenotazioni.length > 0">
                        <div v-for="(p, i) in prenotazioni" :key="'pr-'+i"
                             class="pratica-card pratica-provvisorio mb-2"
                             @click.stop="handleClickCalendario($event, p)">
                            <div class="pratica-corpo flex-grow-1">
                                <div class="d-flex align-center justify-space-between mb-1">
                                    <span class="pratica-cliente">
                                        <template v-if="p.oratore">{{ p.oratore.nominativo }}</template>
                                        <template v-else-if="p.isExtra">{{ p.oratoreExtra }}</template>
                                    </span>
                                    <div class="d-flex align-center">
                                        <v-chip v-if="p.oratore && p.oratore.isAnziano"      x-small dark color="teal" class="mr-1">A</v-chip>
                                        <v-chip v-if="p.oratore && p.oratore.isSM"           x-small dark color="blue darken-1" class="mr-1">SM</v-chip>
                                        <v-chip v-if="p.oratore && p.oratore.isAssistente"   x-small dark color="grey darken-2" class="mr-1">ASSISTENTE</v-chip>
                                        <v-chip v-if="p.oratore && p.oratore.isCoordinatore" x-small dark color="deep-orange darken-4" class="mr-1">ADM</v-chip>
                                        <v-chip v-if="p.oratore && p.oratore.isAdmin"        x-small dark color="orange darken-1" class="mr-1">APP-ADM</v-chip>
                                        <v-icon small color="white" style="opacity:0.5">mdi-dots-vertical</v-icon>
                                    </div>
                                </div>
                                <div class="pratica-data mb-1">{{ p.data | fmtDate }}</div>
                                <div class="pratica-descrizione d-flex align-center">
                                    <v-icon x-small color="white" class="mr-1" style="opacity:0.7">mdi-home-city-outline</v-icon>
                                    <span v-if="p.congregazione">{{ p.congregazione.nome }}</span>
                                </div>
                                <div v-if="p.discorso" class="pratica-note d-flex align-center mt-1">
                                    <v-icon x-small color="white" class="mr-1" style="opacity:0.6">mdi-book-open-outline</v-icon>
                                    <span class="pratica-badge badge-schema mr-1">{{ p.discorso.diId === 99999 ? '-' : p.discorso.diId }}</span>
                                    <span class="pratica-tema">{{ p.discorso.tema }}</span>
                                </div>
                                <div class="da-confermare mt-2">
                                    <v-icon x-small color="#E65100" class="mr-1">mdi-clock-alert-outline</v-icon>
                                    In attesa di conferma
                                </div>
                            </div>
                        </div>
                    </template>
                    <v-card v-else class="rounded-xl elevation-2 px-3 py-4 text-center">
                        <v-icon color="grey lighten-1" size="36">mdi-calendar-check-outline</v-icon>
                        <div class="text-caption grey--text mt-2">Nessuna prenotazione in attesa</div>
                    </v-card>
                </template>

                <!-- ── Banner PayPal (una sola volta) ───────────────── -->
                <div v-if="mostraPaypalPromo && tipoProgramma === '0'"
                     class="mt-3 mb-1 px-3 py-2 rounded text-center"
                     style="background:rgba(0,112,186,0.06);position:relative">
                    <v-btn icon x-small @click="chiudiPaypalPromo" style="position:absolute;top:2px;right:2px">
                        <v-icon x-small color="grey lighten-1">mdi-close</v-icon>
                    </v-btn>
                    <v-icon x-small style="color:#0070ba" class="mb-1">mdi-heart-outline</v-icon>
                    <div class="text-caption mb-2" style="font-size:11px!important;color:#444">
                        Se l'app ti è utile, puoi supportarla con una piccola donazione.
                    </div>
                    <v-btn depressed rounded color="#0070ba" dark x-small @click="apriPaypalDaProgramma">
                        Dona con PayPal
                    </v-btn>
                </div>

            </v-col>
        </v-row>

        <!-- ── Menu contestuale ─────────────────────────────────────── -->
        <v-menu v-model="actionSheetVisible"
                absolute
                :position-x="menuX"
                :position-y="menuY"
                offset-y
                min-width="220"
                rounded="lg"
                :close-on-content-click="true">
            <v-list dense class="py-1">
                <v-subheader class="px-3 text-caption font-weight-bold" style="height:32px">
                    {{ actionSheetTitle }}
                </v-subheader>
                <v-divider />
                <v-list-item v-for="(action, i) in actionSheetItems" :key="i"
                             dense
                             @click="eseguiAzione(action.id)">
                    <v-list-item-icon class="mr-3">
                        <v-icon small :color="action.color || 'primary'">{{ action.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title class="text-body-2" :style="action.color ? `color:${action.color}` : ''">
                        {{ action.label }}
                    </v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- ── Dialog Conferma eliminazione ────────────────────────── -->
        <v-dialog v-model="dialogDelete" max-width="400">
            <v-card class="rounded-xl">
                <v-card-title class="text-subtitle-1 font-weight-bold">Elimina appuntamento</v-card-title>
                <v-card-text>Sei sicuro di voler eliminare questo appuntamento?</v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogDelete = false">Annulla</v-btn>
                    <v-btn color="error" depressed :loading="loadingAction" @click="doElimina">Elimina</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ── Dialog Conferma appuntamento ─────────────────────────── -->
        <v-dialog v-model="dialogConferma" max-width="400">
            <v-card class="rounded-xl">
                <v-card-title class="text-subtitle-1 font-weight-bold">Conferma appuntamento</v-card-title>
                <v-card-text>
                    Confermare l'appuntamento di
                    <strong>{{ selectedProgramma && selectedProgramma.oratore && selectedProgramma.oratore.nominativo }}</strong>?
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogConferma = false">Annulla</v-btn>
                    <v-btn color="success" depressed :loading="loadingAction" @click="doConferma">Conferma</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ── Dialog Cambia Data ────────────────────────────────────── -->
        <v-dialog v-model="dialogCambiaData" max-width="360">
            <v-card class="rounded-xl">
                <v-card-title class="text-subtitle-1 font-weight-bold">Cambia data</v-card-title>
                <v-card-text>
                    <v-date-picker v-model="nuovaDataPicker" no-title scrollable locale="it-IT"
                                   :first-day-of-week="1" color="primary" full-width />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogCambiaData = false">Annulla</v-btn>
                    <v-btn color="primary" depressed :loading="loadingAction" @click="doCambiaData">Salva</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ── Dialog Scambio / Spostamento ─────────────────────────── -->
        <v-dialog v-model="dialogScambio" max-width="380">
            <v-card class="rounded-xl">
                <v-card-title class="text-subtitle-1 font-weight-bold">Data già occupata</v-card-title>
                <v-card-text v-if="tipoProgramma === '0'">
                    La data selezionata ha già un oratore in programma. Vuoi <strong>scambiare</strong> le date dei due oratori?
                </v-card-text>
                <v-card-text v-else>
                    La data selezionata ha già un oratore in programma. Vuoi fare uno <strong>scambio</strong> (i due oratori si scambiano le date) o uno <strong>spostamento semplice</strong> (aggiungi senza scambiare)?
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogScambio = false">Annulla</v-btn>
                    <v-btn v-if="tipoProgramma === '1'" text color="secondary" @click="_eseguiCambiaData(false)">Sposta</v-btn>
                    <v-btn color="primary" depressed @click="_eseguiCambiaData(true)">Scambia</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ── Dialog Tema Visita ────────────────────────────────────── -->
        <v-dialog v-model="dialogTemaVisita" max-width="420">
            <v-card class="rounded-xl">
                <v-card-title class="text-subtitle-1 font-weight-bold">Imposta tema visita</v-card-title>
                <v-card-text>
                    <v-text-field v-model="temaVisitaInput" label="Tema del discorso" outlined dense rounded
                                  placeholder="Es. Il valore della famiglia" class="mt-2" />
                    <v-text-field v-model="canticoVisitaInput" label="Cantico" outlined dense rounded
                                  placeholder="Numero cantico" type="number" />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogTemaVisita = false">Annulla</v-btn>
                    <v-btn color="primary" depressed :loading="loadingAction" @click="doTemaVisita">Salva</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ── Dialog Export ─────────────────────────────────────────── -->
        <v-dialog v-model="dialogExport" max-width="400">
            <v-card class="rounded-xl">
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon left color="primary">mdi-calendar-export</v-icon>
                    Esporta programma
                </v-card-title>
                <v-card-text>
                    <div class="text-caption grey--text mb-3">Scegli il periodo da esportare:</div>
                    <v-text-field v-model="exportDataInizio" label="Dal" type="date" outlined dense rounded class="mb-2" />
                    <v-text-field v-model="exportDataFine" label="Al" type="date" outlined dense rounded />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogExport = false">Annulla</v-btn>
                    <v-btn :color="exportType === 'pdf' ? 'pink' : 'green darken-1'" depressed
                           :loading="exportType === 'pdf' ? loadingPdf : loadingExcel"
                           @click="doExport">
                        <v-icon left small>{{ exportType === 'pdf' ? 'mdi-file-pdf-box' : 'mdi-microsoft-excel' }}</v-icon>
                        Genera
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ── Dialog Export CSV ─────────────────────────────────────── -->
        <v-dialog v-model="dialogCsvExport" max-width="400">
            <v-card class="rounded-xl">
                <v-card-title class="text-subtitle-1 font-weight-bold">
                    <v-icon left color="blue-grey darken-1">mdi-file-delimited-outline</v-icon>
                    Esporta discorsi interni (CSV)
                </v-card-title>
                <v-card-text>
                    <div class="text-caption grey--text mb-3">Scegli il periodo da esportare:</div>
                    <v-text-field v-model="csvDataInizio" label="Dal" type="date" outlined dense rounded class="mb-2" />
                    <v-text-field v-model="csvDataFine" label="Al" type="date" outlined dense rounded />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogCsvExport = false">Annulla</v-btn>
                    <v-btn color="blue-grey darken-1" dark depressed :loading="loadingCsv" @click="doExportCsv">
                        <v-icon left small>mdi-download</v-icon>
                        Scarica CSV
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Snackbars -->
        <v-snackbar v-model="snackSuccess" timeout="2000" color="success">
            <v-icon dark class="mr-1">mdi-checkbox-marked-circle</v-icon>Operazione eseguita.
        </v-snackbar>
        <v-snackbar v-model="snackError" timeout="2500" color="error">
            <v-icon dark class="mr-1">mdi-alert-circle</v-icon>Errore durante l'operazione.
        </v-snackbar>
        <v-snackbar v-model="snackDataMax" timeout="4000" color="warning" top>
            <v-icon dark class="mr-1">mdi-calendar-alert</v-icon>{{ snackDataMaxMsg }}
        </v-snackbar>
        <!-- Dialog promemoria: presidente e lettore -->
        <v-dialog v-model="dialogPromemoria" max-width="360" persistent>
            <v-card rounded="xl" class="pa-2">
                <v-card-title class="text-body-1 font-weight-bold">Genera promemoria</v-card-title>
                <v-card-text class="pb-0">
                    <v-text-field v-model="promemoriaPresidente" label="Presidente (opzionale)"
                        outlined dense hide-details class="mb-3" />
                    <v-text-field v-model="promemoriaLettore" label="Lettore (opzionale)"
                        outlined dense hide-details />
                </v-card-text>
                <v-card-actions class="pt-3">
                    <v-spacer />
                    <v-btn text @click="dialogPromemoria = false; selectedProgramma = null">Annulla</v-btn>
                    <v-btn color="primary" depressed @click="confermaPromemoria">Genera</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar v-model="snackPdf" timeout="-1" color="teal darken-1">
            <div class="d-flex align-center" style="gap:8px">
                <v-icon dark>mdi-file-pdf-box</v-icon>
                <span>Promemoria generato</span>
                <v-btn text dark small class="ml-2" @click="openPromemoria(); snackPdf = false">
                    <v-icon small class="mr-1">mdi-open-in-new</v-icon>Apri
                </v-btn>
                <v-btn icon dark x-small @click="snackPdf = false">
                    <v-icon small>mdi-close</v-icon>
                </v-btn>
            </div>
        </v-snackbar>
        <v-snackbar v-model="snackCopied" timeout="2000" color="info">
            <v-icon dark class="mr-1">mdi-content-copy</v-icon>Dati copiati negli appunti.
        </v-snackbar>

        <!-- ── Dialog Spostamento Massivo ────────────────────────────── -->
        <v-dialog v-model="dialogSpostamentoMassivo" max-width="400" persistent>
            <v-card rounded="xl">
                <v-card-title class="text-subtitle-1 font-weight-bold pt-4 pb-2">
                    <v-icon color="deep-purple" class="mr-2">mdi-calendar-arrow-right</v-icon>
                    Spostamento Massivo
                </v-card-title>
                <v-card-text class="pb-1">
                    <div class="text-caption grey--text mb-3">
                        Sposta tutti gli appuntamenti (confermati e da confermare) nel periodo selezionato.
                    </div>
                    <v-text-field v-model="shiftDataInizio"
                        outlined rounded dense hide-details
                        label="Data inizio" type="date"
                        prepend-inner-icon="mdi-calendar-start"
                        class="mb-2" />
                    <v-text-field v-model="shiftDataFine"
                        outlined rounded dense hide-details
                        label="Data fine" type="date"
                        prepend-inner-icon="mdi-calendar-end"
                        class="mb-4" />
                    <v-radio-group v-model="shiftDirezione" hide-details class="mt-0">
                        <v-radio value="anticipa" color="deep-purple">
                            <template v-slot:label>
                                <span class="text-body-2">
                                    <strong>Anticipa</strong> di 1 giorno
                                    <span class="grey--text ml-1">(Dom → Sab)</span>
                                </span>
                            </template>
                        </v-radio>
                        <v-radio value="posticipa" color="deep-purple" class="mt-2">
                            <template v-slot:label>
                                <span class="text-body-2">
                                    <strong>Posticipa</strong> di 1 giorno
                                    <span class="grey--text ml-1">(Sab → Dom)</span>
                                </span>
                            </template>
                        </v-radio>
                    </v-radio-group>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-btn text rounded @click="dialogSpostamentoMassivo = false">Annulla</v-btn>
                    <v-spacer />
                    <v-btn color="deep-purple" dark rounded depressed
                        :loading="loadingShift"
                        :disabled="!shiftDataInizio || !shiftDataFine"
                        @click="doSpostamentoMassivo">
                        Esegui
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-snackbar v-model="snackShiftSuccess" timeout="3000" color="success">
            <v-icon dark class="mr-1">mdi-check-circle-outline</v-icon>Spostamento completato.
        </v-snackbar>

        <!-- Dialog NWS Export -->
        <v-dialog v-model="dialogNws" max-width="440">
            <v-card class="rounded-xl">
                <v-card-title class="text-subtitle-1 font-weight-bold d-flex align-center" style="gap:8px">
                    <img src="@/assets/nws_logo.webp" width="22" height="22" style="border-radius:5px;background:linear-gradient(rgb(50,143,196),rgb(78,197,212));padding:2px" />
                    Esportazione per NW Scheduler
                    <v-chip x-small color="orange" dark class="ml-1" style="font-size:10px">BETA</v-chip>
                </v-card-title>
                <v-card-text class="text-body-2">
                    <div class="mb-2">Verranno prodotti file CSV da caricare in NW Scheduler.</div>
                    <div class="mb-1 font-weight-medium">Si consiglia di esportare e caricare nell'ordine:</div>
                    <ol class="pl-4 mb-3" style="line-height:1.8">
                        <li><strong>Congregazioni:</strong> unico file CSV</li>
                        <li><strong>Oratori:</strong> file ZIP con un CSV per ogni congregazione <span class="grey--text">(il sw non consente un caricamento massivo)</span></li>
                        <li class="primary--text font-weight-bold">Programma Interno: unico file CSV. Su NW Scheduler indicare <code class="font-weight-regular">dd/mm/yyyy</code> come formato data &nbsp;<v-chip x-small color="primary" dark>questa esportazione</v-chip></li>
                    </ol>
                    <v-alert dense text type="info" class="text-caption mb-1">
                        Si consiglia di fare alcune prove su NW Scheduler con porzioni dei file generati.
                    </v-alert>
                    <v-alert dense text type="warning" class="text-caption mb-1">
                        Se si effettua solo l'import del programma interno senza aver completato i punti 1 e 2, gli oratori con più di 2 token nel nome (es. Mario De Rossi) verranno importati scorrettamente da NW Scheduler e dovranno essere corretti manualmente.
                    </v-alert>
                    <v-alert dense text type="warning" class="text-caption mb-0">
                        Al momento NW Scheduler non consente l'import del programma esterno.
                    </v-alert>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="dialogNws = false">Annulla</v-btn>
                    <v-btn color="primary" depressed @click="dialogNws = false; esportaCsv()">
                        <v-icon left small>mdi-arrow-right</v-icon>Avanti
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import { callService, callPost, gLocalData, gCheckData, gServerData, gDataCalendar, Snack, SUPERADMIN_CODE } from '@/modules/utilities.js';
import { bus } from '@/main.js';
import ChipLoginOratore from '@/components/ChipLoginOratore.vue';

export default {
    components: { ChipLoginOratore },
    inject: {
        setDetailProgramma: { default: null },
    },
    filters: {
        fmtDate(date) {
            if (!date) return '';
            const d = date instanceof Date ? date : new Date(date);
            return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' });
        },
        fmtDateYear(date) {
            if (!date) return '';
            const d = date instanceof Date ? date : new Date(date);
            return d.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        }
    },
    data: () => ({
        weekdays: ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'],
        value: new Date(),
        loading: false,
        loadingAction: false,
        loadingPersonali: false,
        loadingPrenotazioni: false,
        loadingPdf: false,
        loadingExcel: false,
        loadingCsv: false,
        exportFilePdf: '',
        exportFileExcel: '',
        dialogNws: false,
        dialogCsvExport: false,
        csvDataInizio: gDataCalendar(new Date()),
        csvDataFine: gDataCalendar(new Date(new Date().getFullYear(), new Date().getMonth() + 3, 0)),
        eventi: [],
        tipoProgramma: '0',
        congregazioni: [],
        congregazioneVista: null,
        selectedDate: new Date(),
        selectedDayEvents: [],

        // Menu contestuale
        actionSheetVisible: false,
        actionSheetItems: [],
        actionSheetTitle: '',
        selectedProgramma: null,
        menuX: 0,
        menuY: 0,

        // Dialogs
        dialogDelete: false,
        dialogConferma: false,
        dialogCambiaData: false,
        nuovaDataPicker: null,
        dialogScambio: false,
        programmaConflitto: null,
        dialogTemaVisita: false,
        temaVisitaInput: '',
        canticoVisitaInput: '',
        dialogExport: false,
        exportType: 'pdf',
        exportDataInizio: gDataCalendar(new Date()),
        exportDataFine: gDataCalendar(new Date(new Date().getFullYear(), new Date().getMonth() + 3, 0)),

        // Snacks
        snackSuccess: false,
        snackError: false,
        snackDataMax: false,
        snackDataMaxMsg: '',
        snackCopied: false,
        snackPdf: false,
        promemoriaPdf: '',
        dialogPromemoria: false,
        promemoriaPresidente: '',
        promemoriaLettore: '',

        // Personali
        listaOratori: [],
        loadingOratori: false,
        oratorePersonale: null,
        periodoFuturo: true,
        programmiPersonali: [],

        // Prenotazioni
        prenotazioni: [],

        // Spostamento massivo
        dialogSpostamentoMassivo: false,
        shiftDataInizio: '',
        shiftDataFine: '',
        shiftDirezione: 'anticipa',
        loadingShift: false,
        snackShiftSuccess: false,

        // PayPal promo (mostra una volta sola)
        mostraPaypalPromo: false,
    }),

    computed: {
        isTablet() {
            return this.$vuetify.breakpoint.mdAndUp;
        },
        paypalEnabled() {
            return process.env.VUE_APP_PAYPAL === 'S' && !!process.env.VUE_APP_PAYPAL_LINK;
        },
        isCollaboratore() {
            return this.$store.state.isCollaboratore || false;
        },
        isAdmin() {
            const o = this.$store.state.oratore;
            if (!o) return false;
            return o.codice == SUPERADMIN_CODE || o.isAdmin;
        },
        isSuperAdmin() {
            const o = this.$store.state.oratore;
            return !!o && o.codice == SUPERADMIN_CODE;
        },
        isSpostamentoAbilitato() {
            const o = this.$store.state.oratore;
            if (!o) return false;
            return o.codice == SUPERADMIN_CODE || o.isAdmin || o.isCoordinatore;
        },
        idCongregazioneAttiva() {
            return this.congregazioneVista || this.$store.state.idCongregazione;
        },
        isCoordinatore() {
            if (this.isCollaboratore) return false;
            const o = this.$store.state.oratore;
            if (!o) return false;
            return o.codice == SUPERADMIN_CODE || o.isAdmin || o.isCoordinatore || o.isAssistente;
        },
        // può vedere gli appuntamenti degli altri oratori della propria congregazione
        puoVedereAltriOratori() {
            const o = this.$store.state.oratore;
            if (!o) return false;
            return o.codice == SUPERADMIN_CODE || o.isAdmin || o.isCoordinatore || o.isAssistente;
        },
        sorvegliante() {
            return this.$store.state.circoscrizione?.sorvegliante || null;
        },
        circoscrizionePrenotazione() {
            return this.$store.state.circoscrizione?.prenotazione === true;
        },
        selectedDayHasSpeciale() {
            return this.selectedDayEvents.some(e =>
                e.programma.isCongresso || e.programma.isVisita || e.programma.isSpeciale ||
                e.programma.isStandard || e.programma.isExtra);
        },
        isCalendario() {
            return this.tipoProgramma === '0' || this.tipoProgramma === '1';
        },
        monthTitle() {
            return this.value.toLocaleDateString('it-IT', { year: 'numeric', month: 'long' });
        },
        // Mappa data → eventi: calcolata una volta sola quando cambia this.eventi
        eventiMap() {
            const map = {};
            for (const e of this.eventi) {
                const k = this.dateKey(new Date(e.programma.data));
                if (!map[k]) map[k] = [];
                map[k].push(e);
            }
            return map;
        },
        todayKey() {
            return this.dateKey(new Date());
        },
        selectedDateKey() {
            return this.selectedDate ? this.dateKey(this.selectedDate) : null;
        },
        calendarDays() {
            const year = this.value.getFullYear();
            const month = this.value.getMonth();
            const days = [];
            const firstDay = new Date(year, month, 1);
            let startDow = (firstDay.getDay() + 6) % 7;
            const prevLast = new Date(year, month, 0).getDate();
            const todayTs = new Date().setHours(0,0,0,0);
            for (let i = startDow - 1; i >= 0; i--) {
                const d = new Date(year, month - 1, prevLast - i);
                days.push({ date: d, dayOfMonth: prevLast - i, currentMonth: false, key: this.dateKey(d) });
            }
            const lastDay = new Date(year, month + 1, 0).getDate();
            for (let i = 1; i <= lastDay; i++) {
                const d = new Date(year, month, i);
                days.push({ date: d, dayOfMonth: i, currentMonth: true, future: d.getTime() > todayTs, key: this.dateKey(d) });
            }
            let toAdd = 42 - days.length;
            if (toAdd < 0) toAdd = 49 - days.length;
            for (let i = 1; i <= toAdd; i++) {
                const d = new Date(year, month + 1, i);
                days.push({ date: d, dayOfMonth: i, currentMonth: false, future: true, key: this.dateKey(d) });
            }
            const weeks = [];
            for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
            return weeks;
        }
    },

    methods: {
        // ── Navigazione calendario ────────────────────────────────────
        goToToday() {
            this.value = new Date();
            this.clickDate({ date: new Date(), future: true, currentMonth: true });
        },
        changeMonth(delta) {
            this.value = new Date(this.value.getFullYear(), this.value.getMonth() + delta, 1);
            this.selectedDate = null;
            this.selectedDayEvents = [];
            if (this.isTablet && this.setDetailProgramma) this.setDetailProgramma(null);
        },

        // ── Helper chiave data ────────────────────────────────────────
        dateKey(date) {
            const d = date instanceof Date ? date : new Date(date);
            return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        },

        // ── Classi celle ─────────────────────────────────────────────
        classeCella(day) {
            const isSel  = day.key === this.selectedDateKey;
            const isOggi = day.key === this.todayKey;
            const evDay  = this.eventiMap[day.key] || [];
            const ext    = day.currentMonth ? '' : ' cella-esterna';
            const sel    = isSel ? ' cella-sel' : '';
            const oggiCls = isOggi ? ' cella-oggi' : '';

            if (evDay.some(e => e.programma.isProvvisorio))
                return `cella-provvisorio${oggiCls}${sel}${ext}`;
            if (evDay.some(e => e.programma.isCongresso || e.programma.isSpeciale || e.programma.isVisita))
                return `cella-speciale${oggiCls}${sel}${ext}`;
            if (evDay.length)
                return `cella-evento${oggiCls}${sel}${ext}`;
            if (isOggi)
                return `cella-oggi${sel}`;
            return `cella-normale${sel}${ext}`;
        },
        stileCella(day) {
            const isOggi  = day.key === this.todayKey;
            const isSel   = day.key === this.selectedDateKey;
            const primary = this.$vuetify.theme.currentTheme.primary;
            const evDay   = this.eventiMap[day.key] || [];
            const hasEvent = evDay.length > 0;

            if (isOggi) {
                if (hasEvent) {
                    // oggi con evento: colore CSS dell'evento + ring primary per evidenziare oggi
                    if (isSel)
                        return { boxShadow: `inset 0 0 0 2px rgba(255,255,255,0.75), 0 0 0 4px ${primary}` };
                    return { boxShadow: `0 0 0 2.5px ${primary}` };
                } else {
                    // oggi senza evento: sfondo primary come prima
                    const shadow = isSel
                        ? `inset 0 0 0 2px rgba(255,255,255,0.75), 0 0 0 3px ${primary}`
                        : 'none';
                    return { backgroundColor: primary, color: '#fff', boxShadow: shadow };
                }
            }
            if (isSel) {
                if (hasEvent) return { boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${primary}` };
                return { boxShadow: `0 0 0 3px ${primary}` };
            }
            return {};
        },
        hasProvisional(day) {
            return (this.eventiMap[day.key] || []).some(e => e.programma.isProvvisorio);
        },
        hasEvent(day) {
            return !!(this.eventiMap[day.key]?.length);
        },
        hasSpeciale(day) {
            return (this.eventiMap[day.key] || []).some(e =>
                e.programma.isCongresso || e.programma.isSpeciale || e.programma.isVisita);
        },
        getEventsForDay(date) {
            return this.eventiMap[this.dateKey(date)] || [];
        },
        _applyTalkNotification(payload) {
            // payload = "tab|YYYYMMDD"
            const parts = (payload || '').split('|');
            if (parts.length < 2) return;
            const raw = parts[1];
            if (!raw || raw.length < 8) return;
            this.tipoProgramma = parts[0];
            const date = new Date(
                parseInt(raw.substring(0, 4)),
                parseInt(raw.substring(4, 6)) - 1,
                parseInt(raw.substring(6, 8))
            );
            this.selectedDate = date;
            this.selectedDayEvents = this.getEventsForDay(date);
        },
        clickDate(day) {
            if (!day.currentMonth) {
                this.value = new Date(day.date.getFullYear(), day.date.getMonth(), 1);
            }
            this.selectedDate = day.date;
            this.selectedDayEvents = this.getEventsForDay(day.date);
            if (this.isTablet && this.setDetailProgramma) {
                if (this.selectedDayEvents.length === 1) {
                    this.goToProgramma(this.selectedDayEvents[0].programma);
                } else {
                    this.setDetailProgramma(null);
                }
            }
        },

        // ── Navigazione verso form ────────────────────────────────────
        _checkDataMax(data) {
            const circ = this.$store.state.circoscrizione;
            if (!circ) return true;

            let dataMax = null;
            const mesi = circ.mesiMaxAppuntamenti || 0;
            if (mesi > 0) {
                // Calcola dinamicamente: ultimo giorno del mese N a partire dall'inizio del mese prossimo
                const oggi = new Date();
                const inizioMeseProssimo = new Date(oggi.getFullYear(), oggi.getMonth() + 1, 1);
                const fine = new Date(inizioMeseProssimo);
                fine.setMonth(fine.getMonth() + mesi);
                fine.setDate(fine.getDate() - 1);
                dataMax = fine;
            } else if (circ.dataMaxAppuntamenti) {
                dataMax = new Date(circ.dataMaxAppuntamenti);
            }

            if (!dataMax) return true;
            if (data > dataMax) {
                const dataFmt = dataMax.toLocaleDateString('it-IT');
                this.snackDataMaxMsg = `Non è possibile fissare appuntamenti oltre il ${dataFmt}`;
                this.snackDataMax = true;
                return false;
            }
            return true;
        },
        goToNewStandardProgramma() {
            const data = this.selectedDate || new Date();
            if (!this._checkDataMax(data)) return;
            const tipo = this.tipoProgramma === '0' ? 'interno' : 'esterno';
            if (this.isTablet && this.setDetailProgramma) {
                this.setDetailProgramma({ type: 'standard', data: data.toISOString(), tipo, idCongregazione: this.idCongregazioneAttiva });
            } else {
                this.$router.push({ name: 'programma-new', query: { data: data.toISOString(), tipo, idCong: this.idCongregazioneAttiva } });
            }
        },
        goToNewExtraProgramma() {
            const data = this.selectedDate || new Date();
            if (!this._checkDataMax(data)) return;
            if (this.isTablet && this.setDetailProgramma) {
                const type = this.tipoProgramma === '0' ? 'extra-interno' : 'extra-esterno';
                this.setDetailProgramma({ type, data: data.toISOString(), idCongregazione: this.idCongregazioneAttiva });
            } else if (this.tipoProgramma === '0') {
                this.$router.push({ name: 'programma-extra-interno', query: { data: data.toISOString(), idCong: this.idCongregazioneAttiva } });
            } else {
                this.$router.push({ name: 'programma-extra-esterno', query: { data: data.toISOString(), idCong: this.idCongregazioneAttiva } });
            }
        },
        goToProgramma(programma) {
            if (this.isTablet && this.setDetailProgramma) {
                if (programma.isExtra) {
                    const type = this.tipoProgramma === '0' ? 'extra-interno' : 'extra-esterno';
                    this.setDetailProgramma({ type, id: programma.id, idCongregazione: this.idCongregazioneAttiva });
                } else {
                    const tipo = this.tipoProgramma === '0' ? 'interno' : 'esterno';
                    this.setDetailProgramma({ type: 'standard', id: programma.id, tipo, idCongregazione: this.idCongregazioneAttiva });
                }
            } else if (programma.isExtra) {
                const name = this.tipoProgramma === '0' ? 'programma-extra-interno' : 'programma-extra-esterno';
                this.$router.push({ name, params: { id: programma.id }, query: { idCong: this.idCongregazioneAttiva } });
            } else {
                const tipo = this.tipoProgramma === '0' ? 'interno' : 'esterno';
                this.$router.push({ name: 'programma-edit', params: { id: programma.id, tipo }, query: { idCong: this.idCongregazioneAttiva } });
            }
        },

        onCongChange() {
            this.$nextTick(() => { if (this.$refs.congSelect) this.$refs.congSelect.blur(); });
            this.doRefresh();
        },

        // ── Caricamento dati ──────────────────────────────────────────
        async internalCreated(snack) {
            const param = [this.$store.state.idCircoscrizione];
            this.congregazioni = await callService('/api/congregazione/lista', param);
            const tipo = this.tipoProgramma === '0' ? 'interno' : 'esterno';
            const data = await callService(`/api/programma/${tipo}`, [this.$store.state.idCircoscrizione, this.idCongregazioneAttiva]);
            this.eventi = (data || []).map(p => ({
                name: p.data, start: new Date(p.data),
                color: p.isProvvisorio ? 'orange' : (p.isStandard || p.isExtra) ? 'teal' : 'deep-orange darken-4',
                timed: false, programma: p
            }));
            this.snackSuccess = (snack === Snack.success);
            this.snackError   = (snack === Snack.error);
            if (this.selectedDate) {
                this.selectedDayEvents = this.getEventsForDay(this.selectedDate);
            }
        },

        async checkTipoProgramma() {
            if (this.isTablet && this.setDetailProgramma) this.setDetailProgramma(null);
            if (this.tipoProgramma === '2') {
                if (this.puoVedereAltriOratori) {
                    // Privilegiati: carica lista oratori ma non pre-seleziona nessuno
                    if (this.listaOratori.length === 0) await this.loadOratori();
                    // Non caricare nulla: aspetta che scelgano un oratore
                } else {
                    // Utente normale: fisso sul proprio profilo, carica subito
                    this.oratorePersonale = this.$store.state.oratore;
                    await this.loadPersonali();
                }
            } else if (this.tipoProgramma === '3') {
                await this.loadPrenotazioni();
            } else {
                this.loading = true;
                await this.internalCreated(Snack.nothing);
                if (this.selectedDate) {
                    this.selectedDayEvents = this.getEventsForDay(this.selectedDate);
                    if (this.isTablet && this.setDetailProgramma) {
                        if (this.selectedDayEvents.length === 1) {
                            this.goToProgramma(this.selectedDayEvents[0].programma);
                        } else {
                            this.setDetailProgramma(null);
                        }
                    }
                }
                this.loading = false;
            }
        },

        async saveNewProgramma(type) {
            this.loading = true;
            if (!this.$store.state.oratore) { this.snackError = true; this.loading = false; return; }
            try {
                const paramJson = {
                    partition: this.$store.state.partition,
                    username: this.$store.state.oratore.codice,
                    token: this.$store.state.oratore.token,
                    idCircoscrizione: this.$store.state.idCircoscrizione,
                    programma: {
                        data: this.selectedDate,
                        isCongresso: type === 'isCongresso',
                        isVisita:    type === 'isVisita',
                        isSpeciale:  type === 'isSpeciale',
                        congregazione: { id: this.idCongregazioneAttiva }
                    },
                    interno: this.tipoProgramma === '0'
                };
                const response = await callPost('/api/programma/salva', paramJson);
                bus.$emit('add-programma', response);
                this.selectedDayEvents = this.getEventsForDay(this.selectedDate);
                this.snackSuccess = true;
            } catch { this.snackError = true; }
            finally { this.loading = false; }
        },

        // ── Personali ─────────────────────────────────────────────────
        async loadOratori() {
            this.loadingOratori = true;
            try {
                const lista = await callService('/api/oratore/oratoriCongregazione', [
                    this.$store.state.idCircoscrizione,
                    this.idCongregazioneAttiva
                ]);
                this.listaOratori = (lista || []).sort((a, b) =>
                    (a.nominativo || '').localeCompare(b.nominativo || '', 'it'));
            } catch { this.listaOratori = []; }
            finally { this.loadingOratori = false; }
        },
        async loadPersonali() {
            if (!this.oratorePersonale) return;
            this.loadingPersonali = true;
            try {
                const dataInizio = this.periodoFuturo ? new Date().toISOString() : new Date(2000, 0, 1).toISOString();
                const dataFine   = new Date(2099, 11, 31).toISOString();
                const base = {
                    partition: this.$store.state.partition,
                    username: this.$store.state.oratore.codice,
                    token: this.$store.state.oratore.token,
                    idCircoscrizione: this.$store.state.idCircoscrizione,
                    idCongregazione: this.idCongregazioneAttiva,
                    dataInizio, dataFine,
                    idOratore: this.oratorePersonale.id,
                    stampa: false, pdf: false,
                };
                const [interni, esterni] = await Promise.all([
                    callPost('/api/programma/ricerca', { ...base, interno: true }),
                    callPost('/api/programma/ricerca', { ...base, interno: false }),
                ]);
                // Unisci le due liste, deduplica per id (preferisce la copia esterno che ha
                // congregazione=destinazione con dati completi), poi determina interno/esterno
                // in base alla regola reale: congregazione.id === idCongregazione → interno.
                const idCong = this.idCongregazioneAttiva;
                const tutti = [
                    ...(esterni || []),
                    ...(interni  || []),
                ];
                const seen = new Set();
                const deduped = tutti
                    .filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true; })
                    .map(p => ({ ...p, tipoPersonale: p.congregazione?.id === idCong ? 'interno' : 'esterno' }));
                this.programmiPersonali = deduped.sort((a, b) => new Date(a.data) - new Date(b.data));
            } catch { this.snackError = true; }
            finally { this.loadingPersonali = false; }
        },
        classeCardPersonale(p) {
            if (p.isProvvisorio) return 'pratica-provvisorio';
            return p.tipoPersonale === 'interno' ? 'pratica-normale' : 'pratica-esterno';
        },

        // ── Prenotazioni ──────────────────────────────────────────────
        async loadPrenotazioni() {
            this.loadingPrenotazioni = true;
            try {
                const lista = await callService('/api/programma/prenotazioni', [
                    this.$store.state.idCircoscrizione,
                    this.idCongregazioneAttiva
                ]);
                this.prenotazioni = lista || [];
            } catch { this.snackError = true; }
            finally { this.loadingPrenotazioni = false; }
        },

        // ── Menu contestuale ──────────────────────────────────────────
        openActionSheet(e, programma) {
            this.menuX = e.clientX;
            this.menuY = e.clientY;
            this.selectedProgramma = programma;
            const isInterno  = this.tipoProgramma === '0';
            const isEsterno  = this.tipoProgramma === '1';
            const orId = this.$store.state.oratore?.id;
            const isOwner    = programma.oratore && programma.oratore.id === orId;
            const items = [];

            // Titolo header
            if (programma.isVisita) this.actionSheetTitle = 'Visita del Sorvegliante';
            else if (programma.isCongresso) this.actionSheetTitle = 'Assemblea / Congresso';
            else if (programma.isSpeciale) this.actionSheetTitle = 'Evento Speciale';
            else if (programma.oratore) this.actionSheetTitle = programma.oratore.nominativo;
            else if (programma.oratoreExtra) this.actionSheetTitle = programma.oratoreExtra;
            else this.actionSheetTitle = 'Appuntamento';

            // Modifica
            // Modifica: solo su smartphone (su tablet il click apre già direttamente)
            if (!this.isTablet && !programma.isVisita && !programma.isCongresso && !programma.isSpeciale) {
                if (this.isCoordinatore || isOwner) {
                    items.push({ id: 'modifica', icon: 'mdi-pencil-outline', label: 'Modifica' });
                }
            }
            // Tema visita
            if (programma.isVisita && this.isCoordinatore && isInterno) {
                items.push({ id: 'temaVisita', icon: 'mdi-text-box-edit-outline', label: 'Imposta tema visita' });
            }
            // Conferma (esterno/prenotazione provvisorio, coordinatore)
            const isPrenotazione = this.tipoProgramma === '3';
            if (programma.isProvvisorio && (isEsterno || isPrenotazione) && this.isCoordinatore) {
                items.push({ id: 'conferma', icon: 'mdi-check-circle-outline', label: 'Conferma', color: 'success' });
            }
            // Promemoria PDF (solo interno, standard/extra/visita)
            if (isInterno && (programma.isStandard || programma.isExtra || programma.isVisita)) {
                items.push({ id: 'promemoria', icon: 'mdi-printer-outline', label: 'Genera promemoria PDF' });
            }
            // Cambia data
            if (this.isCoordinatore && !programma.isVisita && !programma.isCongresso && !programma.isSpeciale) {
                items.push({ id: 'cambiaData', icon: 'mdi-calendar-edit', label: 'Cambia data' });
            }
            // Copia discorso
            if (!programma.isVisita && !programma.isCongresso && !programma.isSpeciale) {
                items.push({ id: 'copiaDiscorso', icon: 'mdi-content-copy', label: 'Copia informazioni discorso' });
            }
            // Elimina
            if (this.isCoordinatore) {
                items.push({ id: 'elimina', icon: 'mdi-trash-can-outline', label: 'Elimina', color: '#e53935' });
            }

            this.actionSheetItems = items;
            this.actionSheetVisible = true;
        },
        handleClickCalendario(e, programma) {
            if (this.isTablet) {
                // Su iPad: apri subito in modifica nel detail panel
                this.goToProgramma(programma);
            }
            // Apri il menu (su tablet senza Modifica, su mobile con)
            this.openActionSheet(e, programma);
        },
        handleClickPersonale(e, programma) {
            if (this.isTablet) {
                // Su iPad: apri subito in modifica nel detail panel
                this.goToProgrammaPersonale(programma);
            }
            // Apri il menu (su tablet senza la voce Modifica, su mobile con)
            this.openActionSheetPersonale(e, programma);
        },
        goToProgrammaPersonale(programma) {
            const tipo = (programma._tipoPersonale || programma.tipoPersonale) === 'interno' ? 'interno' : 'esterno';
            if (this.isTablet && this.setDetailProgramma) {
                if (programma.isExtra) {
                    this.setDetailProgramma({ type: tipo === 'interno' ? 'extra-interno' : 'extra-esterno', id: programma.id, idCongregazione: this.idCongregazioneAttiva });
                } else {
                    this.setDetailProgramma({ type: 'standard', id: programma.id, tipo, idCongregazione: this.idCongregazioneAttiva });
                }
            } else if (programma.isExtra) {
                this.$router.push({ name: tipo === 'interno' ? 'programma-extra-interno' : 'programma-extra-esterno', params: { id: programma.id }, query: { idCong: this.idCongregazioneAttiva } });
            } else {
                this.$router.push({ name: 'programma-edit', params: { id: programma.id, tipo }, query: { idCong: this.idCongregazioneAttiva } });
            }
        },
        openActionSheetPersonale(e, programma) {
            this.menuX = e.clientX;
            this.menuY = e.clientY;
            // Per la lista personali: salva il tipo per azioni
            this.selectedProgramma = { ...programma, _tipoPersonale: programma.tipoPersonale };
            const items = [];
            this.actionSheetTitle = programma.oratore ? programma.oratore.nominativo : 'Appuntamento';

            // Modifica: solo su smartphone (su tablet il click apre già direttamente)
            if (!this.isTablet && !programma.isVisita && !programma.isCongresso && !programma.isSpeciale) {
                items.push({ id: 'modificaPersonale', icon: 'mdi-pencil-outline', label: 'Modifica' });
            }
            if (programma.isProvvisorio && programma.tipoPersonale === 'esterno' && this.isCoordinatore) {
                items.push({ id: 'conferma', icon: 'mdi-check-circle-outline', label: 'Conferma', color: 'success' });
            }
            if (!programma.isVisita && !programma.isCongresso && !programma.isSpeciale) {
                items.push({ id: 'copiaDiscorso', icon: 'mdi-content-copy', label: 'Copia informazioni discorso' });
            }
            if (this.isCoordinatore) {
                items.push({ id: 'elimina', icon: 'mdi-trash-can-outline', label: 'Elimina', color: '#e53935' });
            }
            this.actionSheetItems = items;
            this.actionSheetVisible = true;
        },

        async eseguiAzione(id) {
            this.actionSheetVisible = false;
            await this.$nextTick();
            switch (id) {
                case 'modifica':          this.goToProgramma(this.selectedProgramma); break;
                case 'modificaPersonale': this.goToProgrammaPersonale(this.selectedProgramma); break;
                case 'temaVisita':   this.apreTemaVisita(); break;
                case 'conferma':     this.dialogConferma = true; break;
                case 'promemoria':   this.doPromemoria(); break;
                case 'cambiaData':   this.apreCambiaData(); break;
                case 'copiaDiscorso': this.doCopiaDiscorso(); break;
                case 'elimina':      this.dialogDelete = true; break;
            }
        },

        // ── Azioni ────────────────────────────────────────────────────
        openSpostamentoMassivo() {
            const nextYear = new Date().getFullYear() + 1;
            this.shiftDataInizio = `${nextYear}-01-01`;
            this.shiftDataFine   = `${nextYear}-12-31`;
            this.shiftDirezione  = 'anticipa';
            this.dialogSpostamentoMassivo = true;
        },

        async doSpostamentoMassivo() {
            if (this.shiftDirezione === 'posticipa') {
                const dataFineSpostata = new Date(this.shiftDataFine);
                dataFineSpostata.setDate(dataFineSpostata.getDate() + 1);
                if (!this._checkDataMax(dataFineSpostata)) return;
            }
            this.loadingShift = true;
            try {
                const result = await callPost('/api/programma/spostaMassivo', {
                    partition: this.$store.state.partition,
                    username: this.$store.state.oratore?.codice,
                    token: this.$store.state.oratore?.token,
                    idCircoscrizione: this.$store.state.idCircoscrizione,
                    idCongregazione: this.idCongregazioneAttiva,
                    dataInizio: this.shiftDataInizio,
                    dataFine: this.shiftDataFine,
                    direzione: this.shiftDirezione,  // 'anticipa' | 'posticipa'
                });
                if (result) {
                    this.dialogSpostamentoMassivo = false;
                    this.snackShiftSuccess = true;
                    await this.internalCreated(Snack.nothing);
                } else {
                    this.snackError = true;
                }
            } catch {
                this.snackError = true;
            } finally {
                this.loadingShift = false;
            }
        },

        async doRefresh() {
            this.oratorePersonale = null;
            this.programmiPersonali = [];
            this.listaOratori = [];
            this.loading = true;
            await this.internalCreated(Snack.nothing);
            this.loading = false;
            if (this.tipoProgramma === '2' && this.puoVedereAltriOratori) {
                this.loadingOratori = true;
                await this.loadOratori();
                this.loadingOratori = false;
            } else if (this.tipoProgramma === '3') {
                await this.loadPrenotazioni();
            }
        },

        async doElimina() {
            this.dialogDelete = false;
            if (!this.selectedProgramma) return;
            this.loadingAction = true;
            try {
                const isInterno = this.tipoProgramma === '0' || this.selectedProgramma._tipoPersonale === 'interno';
                const result = await callPost('/api/programma/elimina', {
                    partition: this.$store.state.partition,
                    username:  this.$store.state.oratore.codice,
                    token:     this.$store.state.oratore.token,
                    idCircoscrizione: this.$store.state.idCircoscrizione,
                    programma: {
                        id: this.selectedProgramma.id,
                        congregazione:        this.selectedProgramma.congregazione        ? { id: this.selectedProgramma.congregazione.id }        : null,
                        congregazioneEsterna: this.selectedProgramma.congregazioneEsterna ? { id: this.selectedProgramma.congregazioneEsterna.id } : null,
                    },
                    interno: isInterno
                });
                if (result) {
                    bus.$emit('remove-programma', this.selectedProgramma.id);
                    if (this.isTablet && this.setDetailProgramma) this.setDetailProgramma(null);
                    this.snackSuccess = true;
                    if (this.tipoProgramma === '3') await this.loadPrenotazioni();
                } else { this.snackError = true; }
            } catch { this.snackError = true; }
            finally { this.loadingAction = false; this.selectedProgramma = null; }
        },

        async doConferma() {
            this.dialogConferma = false;
            if (!this.selectedProgramma) return;
            this.loadingAction = true;
            try {
                const result = await callPost('/api/programma/conferma', {
                    partition: this.$store.state.partition,
                    username:  this.$store.state.oratore.codice,
                    token:     this.$store.state.oratore.token,
                    idCircoscrizione: this.$store.state.idCircoscrizione,
                    programma: {
                        id: this.selectedProgramma.id,
                        congregazione:        this.selectedProgramma.congregazione        ? { id: this.selectedProgramma.congregazione.id }        : null,
                        congregazioneEsterna: this.selectedProgramma.congregazioneEsterna ? { id: this.selectedProgramma.congregazioneEsterna.id } : null,
                    },
                    interno: false
                });
                if (result) {
                    bus.$emit('update-programma', result);
                    this.snackSuccess = true;
                    if (this.tipoProgramma === '3') await this.loadPrenotazioni();
                } else { this.snackError = true; }
            } catch { this.snackError = true; }
            finally { this.loadingAction = false; this.selectedProgramma = null; }
        },

        apreCambiaData() {
            if (this.selectedProgramma) {
                this.nuovaDataPicker = gDataCalendar(new Date(this.selectedProgramma.data));
            }
            this.dialogCambiaData = true;
        },
        async doCambiaData() {
            this.dialogCambiaData = false;
            if (!this.selectedProgramma || !this.nuovaDataPicker) return;

            if (!this._checkDataMax(new Date(this.nuovaDataPicker))) return;

            // Controlla se c'è già un programma standard sulla nuova data
            const eventiNuovaData = this.eventiMap[this.nuovaDataPicker] || [];
            const conflitto = eventiNuovaData.find(e =>
                e.programma.id !== this.selectedProgramma.id &&
                !e.programma.isCongresso && !e.programma.isSpeciale &&
                !e.programma.isVisita && !e.programma.isExtra
            );
            if (conflitto) {
                this.programmaConflitto = conflitto.programma;
                this.dialogScambio = true;
                return;
            }

            await this._eseguiCambiaData(false);
        },

        async _eseguiCambiaData(scambio) {
            this.dialogScambio = false;
            if (!this.selectedProgramma || !this.nuovaDataPicker) return;
            this.loadingAction = true;
            const dataOriginale = new Date(this.selectedProgramma.data).toISOString();
            const dataNuova     = new Date(this.nuovaDataPicker).toISOString();
            const baseParam = {
                partition: this.$store.state.partition,
                username:  this.$store.state.oratore.codice,
                token:     this.$store.state.oratore.token,
                idCircoscrizione: this.$store.state.idCircoscrizione,
                idCongregazione:  this.idCongregazioneAttiva,
                interno: this.tipoProgramma === '0'
            };
            try {
                if (scambio && this.programmaConflitto) {
                    // Sposta il programma in conflitto alla data originale
                    const r1 = await callPost('/api/programma/cambiaData', {
                        ...baseParam,
                        idProgramma: this.programmaConflitto.id,
                        nuovaData:   dataOriginale
                    });
                    if (!r1) { this.snackError = true; return; }
                    bus.$emit('remove-programma', this.programmaConflitto.id);
                    bus.$emit('add-programma', r1);
                }
                // Sposta il programma selezionato alla nuova data
                const r2 = await callPost('/api/programma/cambiaData', {
                    ...baseParam,
                    idProgramma: this.selectedProgramma.id,
                    nuovaData:   dataNuova
                });
                if (r2) {
                    bus.$emit('remove-programma', this.selectedProgramma.id);
                    bus.$emit('add-programma', r2);
                    if (this.selectedDate) this.selectedDayEvents = this.getEventsForDay(this.selectedDate);
                    this.snackSuccess = true;
                } else { this.snackError = true; }
            } catch { this.snackError = true; }
            finally {
                this.loadingAction = false;
                this.selectedProgramma = null;
                this.programmaConflitto = null;
            }
        },

        apreTemaVisita() {
            this.temaVisitaInput  = this.selectedProgramma?.teamVisita || '';
            this.canticoVisitaInput = this.selectedProgramma?.cantico ? String(this.selectedProgramma.cantico) : '';
            this.dialogTemaVisita = true;
        },
        async doTemaVisita() {
            this.dialogTemaVisita = false;
            if (!this.selectedProgramma) return;
            this.loadingAction = true;
            try {
                const result = await callPost('/api/programma/temaVisita', {
                    partition: this.$store.state.partition,
                    username:  this.$store.state.oratore.codice,
                    token:     this.$store.state.oratore.token,
                    idCircoscrizione: this.$store.state.idCircoscrizione,
                    idCongregazione:  this.idCongregazioneAttiva,
                    idProgramma: this.selectedProgramma.id,
                    tema:    this.temaVisitaInput,
                    cantico: this.canticoVisitaInput || '0'
                });
                if (result) {
                    bus.$emit('update-programma', result);
                    this.snackSuccess = true;
                } else { this.snackError = true; }
            } catch { this.snackError = true; }
            finally { this.loadingAction = false; this.selectedProgramma = null; }
        },

        doPromemoria() {
            if (!this.selectedProgramma) return;
            this.promemoriaPresidente = '';
            this.promemoriaLettore = '';
            this.dialogPromemoria = true;
        },
        async confermaPromemoria() {
            this.dialogPromemoria = false;
            if (!this.selectedProgramma) return;
            this.loadingAction = true;
            try {
                const idCong = this.selectedProgramma.congregazione?.id || this.idCongregazioneAttiva;
                const presidente = encodeURIComponent(this.promemoriaPresidente || '');
                const lettore = encodeURIComponent(this.promemoriaLettore || '');
                const output = await callService(
                    `/api/print/promemoriaPdf?presidente=${presidente}&lettore=${lettore}`,
                    [this.$store.state.idCircoscrizione, idCong, this.selectedProgramma.id]
                );
                if (output && output.fileOutput) {
                    this.promemoriaPdf = output.fileOutput;
                    this.snackPdf = true;
                } else { this.snackError = true; }
            } catch { this.snackError = true; }
            finally { this.loadingAction = false; this.selectedProgramma = null; }
        },
        openPromemoria() {
            if (!this.promemoriaPdf) return;
            const url = process.env.VUE_APP_DOCUMENT_SERVER + '/' + this.promemoriaPdf;
            if (window.HybridWebView) {
                this.$callMaui('DownloadAndOpenFile', `${url}|promemoria.pdf|nocache`);
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = 'promemoria.pdf';
                a.click();
            }
            this.promemoriaPdf = '';
        },

        doCopiaDiscorso() {
            const p = this.selectedProgramma;
            if (!p) return;
            let info = '';
            info += 'Data: ' + new Date(p.data).toLocaleDateString('it-IT');

            // p.congregazione è SEMPRE il luogo del discorso (la nostra cong. per gli interni,
            // la cong. di destinazione per gli esterni).
            // p.congregazioneEsterna è la cong. di PROVENIENZA del relatore — NON il luogo.
            // Per gli extra non abbiamo l'oggetto congregazione, solo il testo libero.
            if (p.isExtra) {
                // Solo nome testuale, nessun dettaglio
                if (p.congregazioneExtra) info += '\nCongregazione: ' + p.congregazioneExtra;
            } else {
                const cong = p.congregazione;
                if (cong?.nome)      info += '\nCongregazione: ' + cong.nome;
                if (cong?.indirizzo) info += '\nIndirizzo: ' + cong.indirizzo;
                if (cong?.ora)       info += '\nOre: ' + cong.ora;
                if (cong?.coordinate) {
                    info += '\nMaps: https://maps.google.com/?q=' + cong.coordinate;
                }
            }

            const nomeOratore = p.oratoreExtra || p.oratore?.nominativo;
            if (nomeOratore) info += '\nOratore: ' + nomeOratore;
            if (p.discorso) {
                info += '\nSchema N.: ' + p.discorso.diId;
                info += '\nTema: ' + p.discorso.tema;
            }
            if (p.cantico > 0) info += '\nCantico: ' + p.cantico;
            navigator.clipboard?.writeText(info).then(() => { this.snackCopied = true; })
                .catch(() => { this.snackError = true; });
            this.selectedProgramma = null;
        },

        // ── Export ────────────────────────────────────────────────────
        esportaPdf() {
            this.exportType = 'pdf';
            const start = this.selectedDate || new Date(this.value.getFullYear(), this.value.getMonth(), 1);
            const end   = new Date(start.getFullYear(), start.getMonth() + 3, 0);
            this.exportDataInizio = gDataCalendar(start);
            this.exportDataFine   = gDataCalendar(end);
            this.dialogExport = true;
        },
        esportaExcel() {
            this.exportType = 'excel';
            const start = this.selectedDate || new Date(this.value.getFullYear(), this.value.getMonth(), 1);
            const end   = new Date(start.getFullYear(), start.getMonth() + 3, 0);
            this.exportDataInizio = gDataCalendar(start);
            this.exportDataFine   = gDataCalendar(end);
            this.dialogExport = true;
        },
        esportaCsv() {
            // Propone il giorno cliccato nel calendario come inizio e 2 mesi dopo come fine
            const start = this.selectedDate || new Date(this.value.getFullYear(), this.value.getMonth(), 1);
            const end   = new Date(start.getFullYear(), start.getMonth() + 3, 0);
            this.csvDataInizio = gDataCalendar(start);
            this.csvDataFine   = gDataCalendar(end);
            this.dialogCsvExport = true;
        },
        async doExportCsv() {
            this.dialogCsvExport = false;
            this.loadingCsv = true;
            try {
                const paramJson = {
                    partition:        this.$store.state.partition,
                    username:         this.$store.state.oratore.codice,
                    token:            this.$store.state.oratore.token,
                    idCircoscrizione: this.$store.state.idCircoscrizione,
                    idCongregazione:  this.idCongregazioneAttiva,
                    interno:          true,
                    idOratore:        null,
                    dataInizio:       this.localDayStart(this.csvDataInizio),
                    dataFine:         this.localDayEnd(this.csvDataFine),
                    stampa:           false,
                    Csv:              true,
                };
                const out = await callPost('/api/programma/ricerca', paramJson);
                if (!out || !out.fileOutput) { this.snackError = true; return; }
                const url = process.env.VUE_APP_DOCUMENT_SERVER + '/' + out.fileOutput;
                const filename = out.fileOutput.split('/').pop();
                if (window.HybridWebView) {
                    this.$callMaui('DownloadAndOpenFile', `${url}|${filename}|nocache`);
                } else {
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = filename;
                    a.target = '_blank';
                    a.click();
                }
            } catch { this.snackError = true; }
            finally { this.loadingCsv = false; }
        },
        // Converte YYYY-MM-DD in ISO UTC usando mezzanotte locale (non UTC).
        // new Date(y, m-1, d) è sempre ora locale, a differenza di new Date('YYYY-MM-DD').
        localDayStart(dateStr) {
            const [y, m, d] = dateStr.split('-').map(Number);
            return new Date(y, m - 1, d).toISOString();
        },
        localDayEnd(dateStr) {
            const [y, m, d] = dateStr.split('-').map(Number);
            return new Date(y, m - 1, d, 23, 59, 59).toISOString();
        },
        async doExport() {
            this.dialogExport = false;
            const isPdf = this.exportType === 'pdf';
            if (isPdf) this.loadingPdf = true; else this.loadingExcel = true;
            try {
                const paramJson = {
                    partition: this.$store.state.partition,
                    username:  this.$store.state.oratore.codice,
                    token:     this.$store.state.oratore.token,
                    idCircoscrizione: this.$store.state.idCircoscrizione,
                    idCongregazione:  this.idCongregazioneAttiva,
                    interno:   this.tipoProgramma === '0',
                    idOratore: null,
                    dataInizio: this.localDayStart(this.exportDataInizio),
                    dataFine:   this.localDayEnd(this.exportDataFine),
                    stampa: true,
                    pdf:    isPdf
                };
                const output = await callPost('/api/programma/ricerca', paramJson);
                if (output && output.fileOutput) {
                    if (isPdf) this.exportFilePdf = output.fileOutput;
                    else this.exportFileExcel = output.fileOutput;
                } else { this.snackError = true; }
            } catch { this.snackError = true; }
            finally {
                if (isPdf) this.loadingPdf = false; else this.loadingExcel = false;
            }
        },
        openExportFile(type) {
            const file = type === 'pdf' ? this.exportFilePdf : this.exportFileExcel;
            if (!file) return;
            const url = process.env.VUE_APP_DOCUMENT_SERVER + '/' + file;
            const filename = file.split('/').pop().replace(/_[0-9a-f]{4}\./, '.');
            if (window.HybridWebView) {
                this.$callMaui('DownloadAndOpenFile', `${url}|${filename}|nocache`);
            } else {
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.target = '_blank';
                a.click();
            }
            if (type === 'pdf') this.exportFilePdf = ''; else this.exportFileExcel = '';
        },

        // ── Contatti ──────────────────────────────────────────────────
        hasContatti(p) {
            return (p.oratore && (p.oratore.telefono || p.oratore.mail)) ||
                   (p.congregazione && p.congregazione.latitudine);
        },
        chiudiPaypalPromo() {
            this.mostraPaypalPromo = false;
        },
        apriPaypalDaProgramma() {
            const link = process.env.VUE_APP_PAYPAL_LINK;
            if (link) this.$openUrl(link);
            localStorage.setItem('talk_paypal_donated', '1');
            this.mostraPaypalPromo = false;
        },
        openTel(tel) { if (tel) this.$openUrl('tel:' + tel.replace(/\s/g, '')); },
        openMail(mail) { if (mail) this.$openUrl('mailto:' + mail); },
        openMaps(lat, lng) {
            if (lat && lng) this.$openUrl(`https://maps.google.com/?daddr=${lat},${lng}`);
        },

        // ── Utils ─────────────────────────────────────────────────────
        localData(v) { return gLocalData(v); },
        checkData(d) { return gCheckData(d); },
        serverData(s) { return gServerData(s); },
    },

    watch: {
        '$updaterHub.signals.programma'() {
            const prevDate = this.selectedDate;
            this.internalCreated().then(() => {
                if (prevDate) {
                    this.selectedDate = prevDate;
                    this.selectedDayEvents = this.getEventsForDay(prevDate);
                }
            });
        },
        '$store.state.oratore'(newOratore) {
            if (newOratore) {
                // Reset congregazione quando cambia utente (es. impersonificazione)
                this.congregazioneVista = this.$store.state.idCongregazione;
                // Chiudi il pannello di dettaglio tablet così ProgrammaEdit si rimonta
                // senza inlineIdCongregazione stale dell'utente precedente
                if (this.setDetailProgramma) this.setDetailProgramma(null);
                this.loading = true;
                this.internalCreated(Snack.nothing).then(() => {
                    this.loading = false;
                    const today = new Date();
                    this.selectedDate = today;
                    this.selectedDayEvents = this.getEventsForDay(today);
                    this.$nextTick(() => {
                        if (this.isTablet && this.setDetailProgramma && this.selectedDayEvents.length === 1) {
                            this.goToProgramma(this.selectedDayEvents[0].programma);
                        }
                    });
                });
            }
        }
    },

    async created() {
        if (this.paypalEnabled && !localStorage.getItem('talk_paypal_donated')) {
            const last = parseInt(localStorage.getItem('talk_paypal_last_shown') || '0', 10);
            const msInMonth = 30 * 24 * 60 * 60 * 1000;
            if (Date.now() - last > msInMonth) {
                this.mostraPaypalPromo = true;
                localStorage.setItem('talk_paypal_last_shown', String(Date.now()));
            }
        }

        this.congregazioneVista = this.$store.state.idCongregazione;
        if (this.$store.state.oratore) {
            this.loading = true;
            await this.internalCreated(Snack.nothing);
            this.loading = false;
            // Cold start: se c'è una notifica Talk in sospeso, la gestisco ora
            // che i dati sono caricati (il bus.$emit era andato perso)
            if (window._pendingTalkNotification) {
                this._applyTalkNotification(window._pendingTalkNotification);
                window._pendingTalkNotification = null;
            } else {
                this.selectedDate = new Date();
                this.selectedDayEvents = this.getEventsForDay(this.selectedDate);
                this.$nextTick(() => {
                    if (this.isTablet && this.setDetailProgramma && this.selectedDayEvents.length === 1) {
                        this.goToProgramma(this.selectedDayEvents[0].programma);
                    }
                });
            }
        }

        const vm = this;

        bus.$on('remove-programma', (id) => {
            const idx = vm.eventi.findIndex(e => e.programma.id == id);
            if (idx !== -1) vm.eventi.splice(idx, 1);
            if (vm.selectedDate) vm.selectedDayEvents = vm.getEventsForDay(vm.selectedDate);
            // Rimuovi anche dalla lista personali se presente
            const idxP = vm.programmiPersonali.findIndex(p => p.id == id);
            if (idxP !== -1) vm.programmiPersonali.splice(idxP, 1);
            // Rimuovi anche dalla lista prenotazioni se presente
            const idxPr = vm.prenotazioni.findIndex(p => p.id == id);
            if (idxPr !== -1) vm.prenotazioni.splice(idxPr, 1);
        });

        bus.$on('update-programma', (programma) => {
            const idx = vm.eventi.findIndex(e => e.programma.id == programma.id);
            const evento = {
                name: programma.data, start: new Date(programma.data),
                color: programma.isProvvisorio ? 'orange' : (programma.isStandard || programma.isExtra) ? 'teal' : 'deep-orange darken-4',
                timed: false, programma
            };
            if (idx !== -1) vm.eventi.splice(idx, 1, evento);
            else vm.eventi.push(evento);
            if (vm.selectedDate) vm.selectedDayEvents = vm.getEventsForDay(vm.selectedDate);
        });

        bus.$on('add-programma', (programma) => {
            vm.eventi.push({
                name: programma.data, start: new Date(programma.data),
                color: programma.isProvvisorio ? 'orange' : (programma.isStandard || programma.isExtra) ? 'teal' : 'deep-orange darken-4',
                timed: false, programma
            });
            if (vm.selectedDate) vm.selectedDayEvents = vm.getEventsForDay(vm.selectedDate);
        });

        bus.$on('talk-notification', async (payload) => {
            // Warm/hot start: Programma.vue già montato, ricarico e navigo
            window._pendingTalkNotification = null; // già gestito
            vm.loading = true;
            await vm.internalCreated(Snack.nothing);
            vm.loading = false;
            vm._applyTalkNotification(payload);
        });
    },

    beforeDestroy() {
        bus.$off('remove-programma');
        bus.$off('update-programma');
        bus.$off('add-programma');
        bus.$off('talk-notification');
    }
};
</script>

<style scoped>
/* ── Celle calendario ───────────────────────────────────────── */
.cal-cella {
    height: 34px !important; width: 34px !important; min-width: 34px !important;
    border-radius: 50% !important; margin: auto; cursor: pointer;
    transition: transform 0.12s ease, background-color 0.12s ease;
    user-select: none;
}
.cella-evento    { background-color: #009688 !important; color: #fff !important; }
.cella-provvisorio { background-color: #FB8C00 !important; color: #fff !important; }
.cella-speciale  { background-color: #BF360C !important; color: #fff !important; }
.cella-esterna   { opacity: 0.35; }
.cella-normale:hover, .cella-esterna.cella-normale:hover { background-color: rgba(25,118,210,0.08) !important; opacity:1; }
.cella-oggi:hover, .cella-evento:hover, .cella-provvisorio:hover, .cella-speciale:hover { transform: scale(1.12); opacity: 1; }

/* ── Toggle segmenti ────────────────────────────────────────── */
.tipo-toggle {
    border-radius: 20px !important; overflow: hidden;
    border: 1px solid rgba(0,0,0,0.15) !important;
}
.theme--dark .tipo-toggle { border-color: rgba(255,255,255,0.15) !important; }
.tipo-btn {
    font-size: 0.72rem !important; letter-spacing: 0.02em !important;
    min-width: 72px !important; height: 30px !important; text-transform: none !important;
}

/* ── Toggle periodo ─────────────────────────────────────────── */
.periodo-toggle { border-radius: 14px !important; overflow: hidden; border: 1px solid rgba(0,0,0,0.15) !important; }
.theme--dark .periodo-toggle { border-color: rgba(255,255,255,0.15) !important; }
.periodo-btn { font-size: 0.72rem !important; min-width: 56px !important; height: 26px !important; text-transform: none !important; }

/* ── Quick action buttons ────────────────────────────────────── */
.evento-quick-btn {
    cursor: pointer; border-radius: 10px !important; min-height: 64px;
    transition: box-shadow 0.15s ease, transform 0.15s ease;
    display: flex; flex-direction: column; align-items: center; justify-content: center;
}
.evento-quick-btn:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.15) !important; transform: translateY(-1px); }
.evento-quick-label { font-size: 0.70rem; font-weight: 600; line-height: 1.2; color: rgba(0,0,0,0.65); }
.theme--dark .evento-quick-label { color: rgba(255,255,255,0.7); }
.quick-btn-disabled { opacity: 0.4; cursor: not-allowed !important; pointer-events: none; }

/* ── Card eventi ────────────────────────────────────────────── */
.pratica-card {
    display: flex; border-radius: 10px !important; overflow: hidden;
    box-shadow: 0 1px 4px rgba(0,0,0,0.15); cursor: pointer;
    border-left: 5px solid rgba(0,0,0,0.2);
}
.pratica-normale    { background-color: #00695C; border-left-color: #004D40; }
.pratica-provvisorio { background-color: #FB8C00; border-left-color: #F57C00; }
.pratica-speciale   { background-color: #BF360C; border-left-color: #7F0000; }
.pratica-esterno    { background-color: #00695C; border-left-color: #004D40; }

.pratica-corpo {
    flex: 1; padding: 12px 16px; color: #ffffff;
    display: flex; flex-direction: column;
}
.pratica-cliente { font-size: 0.92rem; font-weight: 700; color: #ffffff; }
.pratica-sorv    { font-size: 0.82rem; font-weight: 400; opacity: 0.85; }
.pratica-data    { font-size: 0.82rem; font-weight: 700; color: #ffffff; text-transform: capitalize; }
.pratica-tema-visita { font-size: 0.78rem; color: rgba(255,255,255,0.80); font-style: italic; display: flex; align-items: center; }
.pratica-descrizione { font-size: 0.82rem; font-weight: 600; color: rgba(255,255,255,0.90); }
.pratica-note    { font-size: 0.75rem; color: rgba(255,255,255,0.60); font-style: italic; }
.pratica-tema    { font-style: italic; color: rgba(255,255,255,0.60); font-size: 0.75rem; line-height: 1.3; }
.da-confermare {
    display: inline-flex; align-self: flex-start; align-items: center;
    font-size: 0.60rem; font-weight: 700; color: #E65100;
    background-color: #FFF8E1; border-radius: 20px; padding: 1px 5px;
}
.attesa-nome { font-size: 0.62rem; color: rgba(255,255,255,0.65); display: flex; align-items: center; }
.pratica-tel { cursor: pointer; color: rgba(255,255,255,0.85); }
.chip-tipo   { font-size: 0.60rem !important; height: 18px !important; background-color: rgba(0,0,0,0.25) !important; }
.pratica-badge.badge-schema {
    font-size: 0.68rem; font-weight: 700; padding: 1px 6px; border-radius: 4px;
    background-color: rgba(0,0,0,0.3); color: #fff; white-space: nowrap;
}

/* ── Combo congregazione ─────────────────────────────────────── */
.cong-select ::v-deep .v-select__selection {
    font-size: 0.875rem !important;
    font-weight: 700 !important;
    color: var(--v-primary-base) !important;
    width: 100%;
    text-align: center;
}
.cong-select ::v-deep .v-select__selections {
    justify-content: center;
}
.cong-select ::v-deep .v-input__slot {
    min-height: 34px !important;
}

/* ── Schermo stretto (Display Zoom / iPhone SE) ─────────────── */
@media (max-width: 360px) {
    .cal-cella {
        height: 26px !important;
        width: 26px !important;
        min-width: 26px !important;
    }
    .tipo-btn {
        min-width: 54px !important;
        max-width: 80px !important;
    }
    .tipo-icon {
        display: none !important;
    }
    .tipo-label {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 54px;
    }
}
</style>
